import { immer } from 'zustand/middleware/immer';
import { persist } from 'zustand/middleware';
import create from 'zustand';
import credentials from 'lib/entities/credentials';
import { store } from 'lib/store';
import { LoginType } from 'features/login/types';
import alertMessage from 'global/AlertMessage';
import {
  requestOtpNewDevice,
  verifyOtpNewDevice,
  requestVerifyIdentity,
  requestVerifyPassword,
  requestVerifyPin,
  requestOtpTrustedDevice,
  verifyOtpTrustedDevice,
  requestSendPrompt,
  getPromptResult,
  verifyPromptRequest,
  requestForgotPin,
} from 'lib/api/trustedDevice';
import { CHANNEL_OTP, TLoginType, PROMPT_STATUS } from './types';
import { calculateRemainingOtpSecond, mapOtpOptions } from './utils';
import {
  trackerIdentityVerificationFailed,
  trackerInputPasswordSuccess,
  trackerInputPinSuccess,
  trackerLoginCompleted,
} from './tracker';

interface State {
  loginType: TLoginType;
  newDeviceOTPConfig: {
    token: string;
    /// OTP Section ///
    otpRecipients: {
      value: CHANNEL_OTP;
      icon: string;
      text: string;
      subText: string;
    }[];
    channel: CHANNEL_OTP;
    otp: string;
    target: string;
    remainingSecond: number;
    /// ----------- ///
    /// API Call State ///
    isSubmitting: boolean;
    errorText: string;
  };
  trustedDevice: {
    token: string;
    deviceName: string;
    /// Prompt Section ///
    promptToken: string;
    promptRemainingSecond: number;
    promptTarget: string;
    promptStatus: PROMPT_STATUS;
    ackPrompToken: string;
    /// ----------- ///
    /// OTP Section ///
    otpRecipients: {
      value: CHANNEL_OTP;
      icon: string;
      text: string;
      subText: string;
    }[];
    channel: CHANNEL_OTP;
    otp: string;
    target: string;
    remainingSecond: number;
    /// ----------- ///
    /// API Call State ///
    isSubmitting: boolean;
    errorText: string;
    errors: any[];
  };
}

interface Actions {
  updateLoginType(type: LoginType): void;
  resetLoginType(): void;
  updateNewDeviceOTPConfig({
    token,
    otpRecipients,
    channel,
    otp,
    target,
    remainingSecond,
    isSubmitting,
    errorText,
  }: {
    token?: string;
    otpRecipients?: {
      value: CHANNEL_OTP;
      icon: string;
      text: string;
      subText: string;
    }[];
    channel?: CHANNEL_OTP;
    otp?: string;
    target?: string;
    remainingSecond?: number;
    isSubmitting?: boolean;
    errorText?: string;
  }): void;
  resetNewDeviceOTPConfig: () => void;
  requestOtpNewDevice(): Promise<void>;
  verifyOtpNewDevice(cb: () => void): Promise<void>;
  updateTrustedDevice({
    token,
    promptToken,
    promptRemainingSecond,
    promptTarget,
    promptStatus,
    ackPrompToken,
    channel,
    deviceName,
    otpRecipients,
    otp,
    target,
    remainingSecond,
    isSubmitting,
    errorText,
    errors,
  }: {
    token?: string;
    promptToken?: string;
    promptRemainingSecond?: number;
    promptTarget?: string;
    promptStatus?: PROMPT_STATUS;
    ackPrompToken?: string;
    deviceName?: string;
    otpRecipients?: {
      value: CHANNEL_OTP;
      icon: string;
      text: string;
      subText: string;
    }[];
    channel?: CHANNEL_OTP;
    otp?: string;
    target?: string;
    remainingSecond?: number;
    isSubmitting?: boolean;
    errorText?: string;
    errors?: any[];
  }): void;
  resetTrustedDevice: () => void;
  requestValidateIdentity({
    ktp,
    dob,
    bankAccount,
    mothersName,
    cb,
  }: {
    ktp: string;
    dob: string;
    bankAccount: string;
    mothersName: string;
    cb: () => void;
  }): Promise<void>;
  requestValidatePassword({
    password,
    cb,
  }: {
    password: string;
    cb: () => void;
  }): Promise<void>;
  requestValidatePin({
    pin,
    cb,
  }: {
    pin: string;
    cb: () => void;
  }): Promise<void>;
  requestForgotPIN(): Promise<void>;
  requestOtpTrustedDevice(): Promise<void>;
  verifyOtpTrustedDevice(cb: () => void): Promise<void>;
  requestPromptTrustedDevice(): Promise<void>;
  getResultPrompTrustedDevice(): Promise<void>;
  verifyPromptTrustedDevice(cb: () => void): Promise<void>;
}

const initialState: State = {
  loginType: LoginType.LOGIN_DATA_TYPE_UNSPECIFIED,
  newDeviceOTPConfig: {
    token: '',
    /// OTP Section ///
    otpRecipients: [],
    channel: CHANNEL_OTP.EMAIL,
    otp: '',
    target: '',
    remainingSecond: 0,
    /// ----------- ///
    /// API Call State ///
    isSubmitting: false,
    errorText: '',
    /// ----------- ///
  },
  trustedDevice: {
    token: '',
    deviceName: '',
    /// Prompt Section ///
    promptToken: '',
    promptRemainingSecond: 0,
    promptTarget: '',
    promptStatus: PROMPT_STATUS.PROMPT_STATUS_UNSPECIFIED,
    ackPrompToken: '',
    /// ----------- ///
    /// OTP Section ///
    otpRecipients: [],
    channel: CHANNEL_OTP.EMAIL,
    otp: '',
    target: '',
    remainingSecond: 0,
    /// ----------- ///
    /// API Call State ///
    isSubmitting: false,
    errorText: '',
    errors: [],
    /// ----------- ///
  },
};

export const useTrustedDeviceStore = create<State & Actions>()(
  persist(
    immer((set, get) => ({
      ...initialState,
      updateLoginType: (loginType) => set({ loginType }),
      resetLoginType: () => set({ loginType: initialState.loginType }),
      updateNewDeviceOTPConfig: ({
        token,
        otpRecipients,
        channel,
        otp,
        target,
        remainingSecond,
        isSubmitting,
        errorText,
      }) =>
        set((draft) => {
          draft.newDeviceOTPConfig.token =
            token ?? draft.newDeviceOTPConfig.token;
          draft.newDeviceOTPConfig.otpRecipients =
            otpRecipients ?? draft.newDeviceOTPConfig.otpRecipients;
          draft.newDeviceOTPConfig.channel =
            channel ?? draft.newDeviceOTPConfig.channel;
          draft.newDeviceOTPConfig.otp = otp ?? draft.newDeviceOTPConfig.otp;
          draft.newDeviceOTPConfig.target =
            target ?? draft.newDeviceOTPConfig.target;
          draft.newDeviceOTPConfig.remainingSecond =
            remainingSecond ?? draft.newDeviceOTPConfig.remainingSecond;
          draft.newDeviceOTPConfig.isSubmitting =
            isSubmitting ?? draft.newDeviceOTPConfig.isSubmitting;
          draft.newDeviceOTPConfig.errorText =
            errorText ?? draft.newDeviceOTPConfig.errorText;
        }),
      resetNewDeviceOTPConfig: () => {
        get().updateNewDeviceOTPConfig(initialState.newDeviceOTPConfig);
      },
      requestOtpNewDevice: async () => {
        try {
          get().updateNewDeviceOTPConfig({ isSubmitting: true, errorText: '' });

          const { token, channel } = get().newDeviceOTPConfig;
          const response = await requestOtpNewDevice({ token, channel });

          get().updateNewDeviceOTPConfig({
            target: response?.data?.data?.target,
            remainingSecond: calculateRemainingOtpSecond(
              response?.data?.data?.next_attempt_time,
            ),
          });
        } catch (err) {
          get().updateNewDeviceOTPConfig({
            remainingSecond: 0,
            errorText: err?.cause?.message,
          });
          // Re-throw error so component can handle
          throw err;
        } finally {
          get().updateNewDeviceOTPConfig({ isSubmitting: false });
        }
      },
      verifyOtpNewDevice: async (cb) => {
        try {
          get().updateNewDeviceOTPConfig({ isSubmitting: true, errorText: '' });
          const { token, otp } = get().newDeviceOTPConfig;
          const response = await verifyOtpNewDevice({ token, otp });
          store.dispatch(
            credentials.actions.loadCredentialsExodus(response?.data?.data),
          );
          trackerLoginCompleted();
          cb();
        } catch (e) {
          get().updateNewDeviceOTPConfig({
            errorText: e?.cause?.message,
          });
        } finally {
          get().updateNewDeviceOTPConfig({ isSubmitting: false });
        }
      },
      updateTrustedDevice: ({
        token,
        promptToken,
        promptRemainingSecond,
        promptTarget,
        ackPrompToken,
        promptStatus,
        channel,
        deviceName,
        otpRecipients,
        otp,
        target,
        remainingSecond,
        isSubmitting,
        errorText,
        errors,
      }) =>
        set((draft) => {
          draft.trustedDevice.token = token ?? draft.trustedDevice.token;

          draft.trustedDevice.promptToken =
            promptToken ?? draft.trustedDevice.promptToken;

          draft.trustedDevice.promptRemainingSecond =
            promptRemainingSecond ?? draft.trustedDevice.promptRemainingSecond;

          draft.trustedDevice.promptTarget =
            promptTarget ?? draft.trustedDevice.promptTarget;

          draft.trustedDevice.ackPrompToken =
            ackPrompToken ?? draft.trustedDevice.ackPrompToken;

          draft.trustedDevice.promptStatus =
            promptStatus ?? draft.trustedDevice.promptStatus;

          draft.trustedDevice.deviceName =
            deviceName ?? draft.trustedDevice.deviceName;

          draft.trustedDevice.otpRecipients =
            otpRecipients ?? draft.trustedDevice.otpRecipients;

          draft.trustedDevice.channel = channel ?? draft.trustedDevice.channel;

          draft.trustedDevice.otp = otp ?? draft.trustedDevice.otp;

          draft.trustedDevice.target = target ?? draft.trustedDevice.target;

          draft.trustedDevice.remainingSecond =
            remainingSecond ?? draft.trustedDevice.remainingSecond;

          draft.trustedDevice.isSubmitting =
            isSubmitting ?? draft.trustedDevice.isSubmitting;

          draft.trustedDevice.errorText =
            errorText ?? draft.trustedDevice.errorText;

          draft.trustedDevice.errors = errors ?? draft.trustedDevice.errors;
        }),
      resetTrustedDevice: () => {
        get().updateTrustedDevice(initialState.trustedDevice);
      },
      requestValidateIdentity: async ({
        ktp,
        dob,
        bankAccount,
        mothersName,
        cb,
      }) => {
        try {
          get().updateTrustedDevice({
            isSubmitting: true,
            errorText: '',
            errors: [],
          });
          const { token } = get().trustedDevice;

          const response: any = await requestVerifyIdentity({
            token,
            identity: ktp,
            birthDate: dob,
            bankAccountNumber: bankAccount,
            motherName: mothersName,
          });
          get().updateTrustedDevice({ token: response?.data?.data?.token });
          cb();
        } catch (err) {
          trackerIdentityVerificationFailed();
          get().updateTrustedDevice({
            errorText: err?.cause?.message,
            errors: err?.cause?.errors,
          });
        } finally {
          get().updateTrustedDevice({ isSubmitting: false });
        }
      },
      requestValidatePassword: async ({ password, cb }) => {
        try {
          get().updateTrustedDevice({ isSubmitting: true, errorText: '' });
          const { token } = get().trustedDevice;

          await requestVerifyPassword({
            token,
            password,
          });
          trackerInputPasswordSuccess();
          cb();
        } catch (err) {
          get().updateTrustedDevice({ errorText: err?.cause?.message });
        } finally {
          get().updateTrustedDevice({ isSubmitting: false });
        }
      },
      requestValidatePin: async ({ pin, cb }) => {
        try {
          const { token } = get().trustedDevice;

          get().updateTrustedDevice({ isSubmitting: true, errorText: '' });
          const response = await requestVerifyPin({ pin, token });
          get().updateTrustedDevice({
            otpRecipients: mapOtpOptions(response?.data?.data?.otp_recipients),
          });
          trackerInputPinSuccess();
          cb();
        } catch (err) {
          get().updateTrustedDevice({ errorText: err?.cause?.message });
        } finally {
          get().updateTrustedDevice({ isSubmitting: false });
        }
      },
      requestForgotPIN: async () => {
        try {
          const { token } = get().trustedDevice;

          get().updateTrustedDevice({ isSubmitting: true, errorText: '' });
          const response = await requestForgotPin({ token });
          alertMessage({
            content: response?.data?.message,
            alertType: 'snackbar-dark',
            messageType: 'success',
            hasCloseIcon: false,
          });
        } catch (err) {
          get().updateTrustedDevice({ errorText: err?.cause?.message });
        } finally {
          get().updateTrustedDevice({ isSubmitting: false });
        }
      },
      requestOtpTrustedDevice: async () => {
        try {
          get().updateTrustedDevice({ isSubmitting: true, errorText: '' });
          const { token, channel } = get().trustedDevice;

          const response = await requestOtpTrustedDevice({ token, channel });
          get().updateTrustedDevice({
            target: response?.data?.data?.target,
            remainingSecond: calculateRemainingOtpSecond(
              response?.data?.data?.next_attempt_time,
            ),
          });
        } catch (err) {
          get().updateTrustedDevice({ errorText: err?.cause?.message });
        } finally {
          get().updateTrustedDevice({ isSubmitting: false });
        }
      },
      verifyOtpTrustedDevice: async (cb) => {
        try {
          get().updateTrustedDevice({ isSubmitting: true, errorText: '' });
          const { token, otp } = get().trustedDevice;

          const response = await verifyOtpTrustedDevice({
            token,
            otp,
          });

          store.dispatch(
            credentials.actions.loadCredentialsExodus(response?.data?.data),
          );
          trackerLoginCompleted();
          cb();
        } catch (err) {
          get().updateTrustedDevice({ errorText: err?.cause?.message });
        } finally {
          get().updateTrustedDevice({ isSubmitting: false });
        }
      },
      requestPromptTrustedDevice: async () => {
        try {
          get().updateTrustedDevice({ isSubmitting: true, errorText: '' });
          const { token } = get().trustedDevice;

          const response = await requestSendPrompt({
            token,
          });
          get().updateTrustedDevice({
            promptToken: response?.data?.data?.prompt_token,
            promptTarget: response?.data?.data?.target,
            promptRemainingSecond: calculateRemainingOtpSecond(
              response?.data?.data?.next_attempt_time,
            ),
          });
        } catch (err) {
          get().updateTrustedDevice({
            errorText: err?.cause?.message,
          });
          throw err;
        } finally {
          get().updateTrustedDevice({ isSubmitting: false });
        }
      },
      getResultPrompTrustedDevice: async () => {
        try {
          const { promptToken } = get().trustedDevice;
          const response = await getPromptResult(promptToken);
          get().updateTrustedDevice({
            ackPrompToken: response?.data?.data?.acknowledge_token,
            promptStatus: response?.data?.data?.status,
          });
        } catch (err) {
          get().updateTrustedDevice({
            errorText: err?.cause?.message,
          });
        }
      },
      verifyPromptTrustedDevice: async (cb) => {
        try {
          get().updateTrustedDevice({ isSubmitting: true, errorText: '' });

          const { token, ackPrompToken } = get().trustedDevice;

          const response = await verifyPromptRequest({
            token,
            acknowledgeToken: ackPrompToken,
          });
          store.dispatch(
            credentials.actions.loadCredentialsExodus(response?.data?.data),
          );
          trackerLoginCompleted();
          cb();
        } catch (err) {
          get().updateTrustedDevice({
            errorText: err?.cause?.message,
          });
        }
      },
    })),
    {
      name: 'trustedDevice',
      // partialize: (state) => ({ loginType: state.loginType }),
    },
  ),
);
